/**
 * 제휴업체 정산/결제 서비스
 * - 컬렉션: partner_payment_requests (결제 요청), partner_receivables (미수금)
 * - 예정 청구: partner_assignments 중 완료됐지만 아직 청구되지 않은 건
 */
import { db } from '../firebase/config';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { SERVICE_CATEGORIES } from '../config/ieumConfig';
import {
  MOVING_TYPE_LABELS,
  CLEANING_TYPES,
  INTERIOR_STYLE_LABELS,
  INTERNET_LABELS,
  INTERNET_SERVICE_TYPES,
} from './customerFormService';

const CLEANING_TYPE_LABELS = CLEANING_TYPES.reduce((acc, t) => ({ ...acc, [t.key]: t.label }), {});
const INTERNET_SERVICE_TYPE_LABELS = INTERNET_SERVICE_TYPES.reduce((acc, t) => ({ ...acc, [t.key]: t.label }), {});

const toDate = (ts) => {
  if (!ts) return null;
  if (ts.seconds) return new Date(ts.seconds * 1000);
  if (ts instanceof Date) return ts;
  return null;
};

const pad2 = (n) => String(n).padStart(2, '0');
const fmtDate = (d) => {
  if (!d) return '';
  return `${d.getFullYear()}. ${d.getMonth() + 1}. ${d.getDate()}. ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/**
 * 결제 요청 수신 목록 (관리자가 금액 지정해서 요청한 건)
 */
// service_request_id → { category, sub-type fields } 단건 lookup (캐시는 호출자 관리)
const fetchSrInfo = async (srId, cache) => {
  if (!srId) return null;
  if (cache.has(srId)) return cache.get(srId);
  try {
    const snap = await getDoc(doc(db, 'service_requests', srId));
    if (!snap.exists()) { cache.set(srId, null); return null; }
    const data = snap.data();
    const info = {
      category: data.category || null,
      movingType: data.moving_type || data.movingType || null,
      cleaningType: data.cleaning_type || data.cleaningType || null,
      internetType: data.internet_type || data.internetType || null,
      internetProvider: data.internet_provider || data.internetProvider || null,
      interiorStyle: data.interior_style || data.interiorStyle || null,
    };
    cache.set(srId, info);
    return info;
  } catch {
    cache.set(srId, null);
    return null;
  }
};

// 카테고리 + 세부종류 → 사용자 표시 라벨
//   moving + pack → "이사 (포장이사)"
//   cleaning + deep → "입주청소 (딥클리닝)"
//   internet_tv + internet_tv + KT → "인터넷TV (인터넷 + TV · KT)"
//   interior + modern → "인테리어 (모던)"
const composeCategoryLabel = (info) => {
  if (!info?.category) return '';
  const base = SERVICE_CATEGORIES[info.category]?.label || info.category;
  let sub = '';
  if (info.category === 'moving' && info.movingType) {
    sub = MOVING_TYPE_LABELS[info.movingType] || info.movingType;
  } else if (info.category === 'cleaning' && info.cleaningType) {
    sub = CLEANING_TYPE_LABELS[info.cleaningType] || info.cleaningType;
  } else if (info.category === 'internet_tv') {
    const parts = [];
    if (info.internetType) parts.push(INTERNET_SERVICE_TYPE_LABELS[info.internetType] || info.internetType);
    if (info.internetProvider) parts.push(INTERNET_LABELS[info.internetProvider] || info.internetProvider);
    sub = parts.join(' · ');
  } else if (info.category === 'interior' && info.interiorStyle) {
    sub = INTERIOR_STYLE_LABELS[info.interiorStyle] || info.interiorStyle;
  }
  return sub ? `${base} (${sub})` : base;
};

// 고객명 마스킹 — 김강민 → 김*민, 이민 → 이*, 이 → 이
const maskName = (name) => {
  if (!name || typeof name !== 'string') return '';
  const trimmed = name.trim();
  if (trimmed.length <= 1) return trimmed;
  if (trimmed.length === 2) return `${trimmed[0]}*`;
  return `${trimmed[0]}*${trimmed.slice(-1)}`;
};

// 서비스명 — 고객명 / 메인 카테고리 (서브) / 평수
const buildServiceItemFromSr = async (srId, customerCache) => {
  if (!srId) return null;
  try {
    const snap = await getDoc(doc(db, 'service_requests', srId));
    if (!snap.exists()) return null;
    const sr = snap.data();
    let customerName = sr.name || '';
    let area = sr.area_pyeong_exact || sr.areaPyeong || sr.area_size || '';
    if (sr.customer_id) {
      let cust;
      if (customerCache.has(sr.customer_id)) cust = customerCache.get(sr.customer_id);
      else {
        try {
          const cSnap = await getDoc(doc(db, 'customers', sr.customer_id));
          cust = cSnap.exists() ? cSnap.data() : null;
        } catch { cust = null; }
        customerCache.set(sr.customer_id, cust);
      }
      if (cust?.name) customerName = cust.name;
      if (!area && cust?.area_pyeong_exact) area = cust.area_pyeong_exact;
    }
    const info = {
      category: sr.category || null,
      movingType: sr.moving_type || sr.movingType || null,
      cleaningType: sr.cleaning_type || sr.cleaningType || null,
      internetType: sr.internet_type || sr.internetType || null,
      internetProvider: sr.internet_provider || sr.internetProvider || null,
      interiorStyle: sr.interior_style || sr.interiorStyle || null,
    };
    const mainLabel = composeCategoryLabel(info);
    // 추가 서비스 (services 배열에서 메인 제외)
    const services = Array.isArray(sr.services) ? sr.services : [];
    const extras = services
      .filter((s) => s !== info.category)
      .map((s) => SERVICE_CATEGORIES[s]?.label || s);
    const fullLabel = extras.length > 0
      ? `${mainLabel} (${extras.join(' / ')})`
      : mainLabel;
    const areaText = area ? (typeof area === 'number' || /^\d+$/.test(String(area)) ? `${area}평` : String(area)) : '';
    return {
      customerName: maskName(customerName) || '-',
      summary: [fullLabel || '-', areaText].filter(Boolean).join(' / '),
    };
  } catch {
    return null;
  }
};

// 결제건 → 관련 SR 들 → serviceItems 리스트
const fetchServiceItems = async (data) => {
  const customerCache = new Map();
  const items = [];
  const seen = new Set();
  const push = async (srId) => {
    if (!srId || seen.has(srId)) return;
    seen.add(srId);
    const item = await buildServiceItemFromSr(srId, customerCache);
    if (item) items.push(item);
  };
  if (data.service_request_id) await push(data.service_request_id);
  if (Array.isArray(data.receivable_ids)) {
    for (const rid of data.receivable_ids) {
      try {
        const snap = await getDoc(doc(db, 'partner_receivables', rid));
        if (snap.exists()) await push(snap.data().service_request_id);
      } catch { /* skip */ }
    }
  }
  return items;
};

// label → 표시용 type 매핑
const normalizeType = (label) => {
  if (!label) return '기타';
  if (/DB|마켓|열람/i.test(label)) return 'DB 열람';
  if (/계약완료|수수료|예약완료/i.test(label)) return '계약완료 수수료';
  return label;
};

// receivable_ids[] → 연결된 SR 정보들 (중복 제거)
const fetchReceivableSrInfos = async (rcvIds, srCache) => {
  if (!Array.isArray(rcvIds) || rcvIds.length === 0) return [];
  const seen = new Set();
  const infos = [];
  for (const rid of rcvIds) {
    try {
      const snap = await getDoc(doc(db, 'partner_receivables', rid));
      if (!snap.exists()) continue;
      const srId = snap.data().service_request_id;
      const info = await fetchSrInfo(srId, srCache);
      if (!info) continue;
      const key = `${info.category}|${info.movingType || ''}|${info.cleaningType || ''}|${info.internetType || ''}|${info.internetProvider || ''}|${info.interiorStyle || ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      infos.push(info);
    } catch { /* skip */ }
  }
  return infos;
};

export const getPaymentRequests = async (partnerId) => {
  if (!partnerId) return [];
  try {
    const q = query(
      collection(db, 'partner_payment_requests'),
      where('partner_id', '==', partnerId),
    );
    const snap = await getDocs(q);
    const srCache = new Map();
    const list = await Promise.all(snap.docs.map(async (d) => {
      const data = d.data();
      const created = toDate(data.created_at || data.createdAt);
      const st = data.status || 'pending';
      const statusMap = { pending: '결제대기', completed: '결제완료', confirming: '입금확인중', rejected: '거절' };
      const productName = data.product_name || data.productName || '';

      // 카테고리 enrich — 본사 외상 청구건은 카테고리 없음, 자동 생성건만 lookup
      let categoryLabel = '';
      if (!productName) {
        const infos = [];
        if (data.service_request_id) {
          const info = await fetchSrInfo(data.service_request_id, srCache);
          if (info) infos.push(info);
        }
        if (Array.isArray(data.receivable_ids) && data.receivable_ids.length > 0) {
          const more = await fetchReceivableSrInfos(data.receivable_ids, srCache);
          more.forEach((i) => infos.push(i));
        }
        const seen = new Set();
        const labels = [];
        infos.forEach((i) => {
          const lbl = composeCategoryLabel(i);
          if (!lbl || seen.has(lbl)) return;
          seen.add(lbl);
          labels.push(lbl);
        });
        categoryLabel = labels.join(', ');
      }

      // 서비스 항목 enrich (관련 SR → 고객명/카테고리/평수)
      const serviceItems = await fetchServiceItems(data);

      // typeBreakdown — 일괄이면 receivables 의 label 별 카운트, 단건이면 본인 label
      let typeBreakdown = '';
      const isBulk = Array.isArray(data.receivable_ids) && data.receivable_ids.length > 0;
      if (isBulk) {
        const counts = {};
        for (const rid of data.receivable_ids) {
          try {
            const snap = await getDoc(doc(db, 'partner_receivables', rid));
            if (!snap.exists()) continue;
            const t = normalizeType(snap.data().label || '');
            counts[t] = (counts[t] || 0) + 1;
          } catch { /* skip */ }
        }
        typeBreakdown = Object.entries(counts).map(([t, c]) => `${t} ${c}건`).join(' / ');
      } else {
        typeBreakdown = normalizeType(productName || data.label || '');
      }

      return {
        id: d.id,
        date: fmtDate(created),
        createdAt: created?.getTime() || 0,
        method: data.method || '카드',
        amount: data.amount || 0,
        status: st,
        statusLabel: statusMap[st] || st,
        memo: data.memo || '',
        label: data.label || '미수 청구',
        productName,
        categoryLabel,
        serviceRequestId: data.service_request_id || null,
        serviceItems,
        typeBreakdown,
        isBulk,
        // 본사 외상 청구는 product_name 입력됨 (관리자 모달 거친 것).
        // DB 마켓 구매·계약완료 수수료 등 자동 생성 결제요청은 product_name 없음.
        source: productName ? 'admin_request' : 'auto',
      };
    }));
    return list.sort((a, b) => b.createdAt - a.createdAt);
  } catch {
    return [];
  }
};

/**
 * 결제 내역 전체 (완료된 요청 + DB 열람 등)
 */
export const getPaymentHistory = async (partnerId) => {
  const requests = await getPaymentRequests(partnerId);
  return requests.filter((r) => r.status === 'completed');
};

/**
 * 미수금 목록 (partner_receivables, status=pending)
 */
export const getReceivables = async (partnerId) => {
  if (!partnerId) return [];
  try {
    const q = query(
      collection(db, 'partner_receivables'),
      where('partner_id', '==', partnerId),
      where('status', '==', 'pending'),
    );
    const snap = await getDocs(q);
    const srCache = new Map();
    const list = await Promise.all(snap.docs.map(async (d) => {
      const data = d.data();
      const created = toDate(data.created_at || data.createdAt);
      const srId = data.service_request_id || null;
      const info = await fetchSrInfo(srId, srCache);
      const categoryLabel = composeCategoryLabel(info);
      return {
        id: d.id,
        date: fmtDate(created),
        createdAt: created?.getTime() || 0,
        label: data.label || '미수금',
        amount: data.amount || 0,
        status: 'unpaid',
        statusLabel: '미납',
        serviceRequestId: srId,
        categoryLabel,
      };
    }));
    return list.sort((a, b) => b.createdAt - a.createdAt);
  } catch {
    return [];
  }
};

/**
 * 예정 청구 (완료된 배정이지만 아직 정산되지 않은 건)
 * - partner_assignments.status === 'completed' && !settled
 */
export const getExpectedCharges = async (partnerId) => {
  if (!partnerId) return [];
  try {
    const q = query(
      collection(db, 'partner_assignments'),
      where('partner_id', '==', partnerId),
      where('status', '==', 'completed'),
    );
    const snap = await getDocs(q);
    const assignments = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      .filter((a) => !a.settled);

    const result = [];
    for (const a of assignments) {
      let customerName = '-';
      try {
        if (a.service_request_id) {
          const srSnap = await getDoc(doc(db, 'service_requests', a.service_request_id));
          if (srSnap.exists()) {
            const sr = srSnap.data();
            const custId = sr.customer_id;
            if (custId) {
              const cSnap = await getDoc(doc(db, 'customers', custId));
              if (cSnap.exists()) customerName = cSnap.data().name || '-';
            }
          }
        }
      } catch { /* skip */ }

      const reserved = toDate(a.reserved_date) || toDate(a.updated_at);
      const catLabel = SERVICE_CATEGORIES[a.category]?.label || a.category || '';
      result.push({
        id: a.id,
        date: fmtDate(reserved),
        createdAt: reserved?.getTime() || 0,
        label: catLabel ? `${customerName} · ${catLabel}` : customerName,
        amount: a.price || 0,
        status: 'expected',
        statusLabel: '예약완료',
        source: 'assignment',
      });
    }
    return result.sort((a, b) => b.createdAt - a.createdAt);
  } catch {
    return [];
  }
};

/**
 * 정산 화면 통합 조회
 * - expected (청구금액 탭): 본사 입력 청구(pending) 만
 * - receivables (미수금 탭): partner_receivables pending
 */
export const getSettlementSummary = async (partnerId) => {
  const [allRequests, history, receivables] = await Promise.all([
    getPaymentRequests(partnerId),
    getPaymentHistory(partnerId),
    getReceivables(partnerId),
  ]);
  const expected = allRequests
    .filter((r) => r.status === 'pending' && r.source === 'admin_request')
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  return { expected, history, receivables };
};
